import { useEffect, useMemo, useRef, useState } from 'react';
import type { BoardSummary, CalendarEvent, EventInput } from '../lib/api';
import { Icon } from '../ui/icons';
import {
  REMINDER_OPTIONS,
  defaultSlotOn,
  fromLocalInput,
  toDateInput,
  toLocalInput,
} from './dates';

const EVENT_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F97316', '#10B981', '#EF4444'];

interface DraftTask {
  id?: string;
  text: string;
  done: boolean;
}

export interface EventEditorProps {
  orgId: string;
  /** null means "create a new event". */
  event: CalendarEvent | null;
  /** Day the editor was opened on, used to seed a new event. */
  day: Date;
  boards: BoardSummary[];
  onSave: (input: EventInput, id: string | null) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onClose: () => void;
}

/**
 * Create/edit form for a calendar entry.
 *
 * The form is deliberately one flat panel rather than a wizard: an event here
 * is a meeting *plus its plan*, and the whole point is being able to see the
 * agenda and the checklist next to the time while filling it in.
 */
export function EventEditor({
  orgId,
  event,
  day,
  boards,
  onSave,
  onDelete,
  onClose,
}: EventEditorProps) {
  const seed = useMemo(() => defaultSlotOn(day), [day]);

  const [title, setTitle] = useState(event?.title ?? '');
  const [description, setDescription] = useState(event?.description ?? '');
  const [allDay, setAllDay] = useState(event?.allDay ?? false);
  const [start, setStart] = useState(event?.start ?? seed.start);
  const [end, setEnd] = useState(event?.end ?? seed.end);
  const [boardId, setBoardId] = useState<string | null>(event?.boardId ?? null);
  const [tasks, setTasks] = useState<DraftTask[]>(
    event?.tasks.map((task) => ({ id: task.id, text: task.text, done: task.done })) ?? [],
  );
  const [remind, setRemind] = useState<number | null>(event?.remindMinutesBefore ?? 15);
  const [color, setColor] = useState(event?.color ?? EVENT_COLORS[0]!);
  const [done, setDone] = useState(event?.done === true);
  const [newTask, setNewTask] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * A state flag alone does not stop a double Enter: both key presses land
   * before React re-renders with `saving`, so the event would be created twice.
   */
  const inFlight = useRef(false);

  const dialogRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Escape closes; a modal that can only be dismissed by mouse is a trap.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const addTask = () => {
    const text = newTask.trim();
    if (text === '') return;
    setTasks((current) => [...current, { text, done: false }]);
    setNewTask('');
  };

  const submit = async () => {
    if (inFlight.current) return;
    const startIso = fromLocalInput(
      allDay ? `${toDateInput(start)}T00:00` : toLocalInput(start),
    );
    const endIso = fromLocalInput(allDay ? `${toDateInput(end)}T23:59` : toLocalInput(end));
    if (!startIso || !endIso) {
      setError('Проверьте дату и время');
      return;
    }

    inFlight.current = true;
    setSaving(true);
    setError(null);
    try {
      await onSave(
        {
          orgId,
          title: title.trim() || 'Без названия',
          description,
          start: startIso,
          end: endIso < startIso ? startIso : endIso,
          allDay,
          boardId,
          tasks: tasks
            .filter((task) => task.text.trim() !== '')
            .map((task) => ({ id: task.id, text: task.text.trim(), done: task.done })),
          remindMinutesBefore: remind,
          color,
          // Sent only when it actually changed: the server restamps `doneAt` on
          // every `done` it receives, so an unrelated edit would reset it.
          ...(!event || done !== (event.done === true) ? { done } : {}),
        },
        event?.id ?? null,
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onPointerDown={(e) => {
        if (!dialogRef.current?.contains(e.target as Node)) onClose();
      }}
    >
      <div
        className="event-editor"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={event ? 'Изменить событие' : 'Новое событие'}
      >
        <header className="event-editor-head">
          <input
            ref={titleRef}
            className="event-title-input"
            placeholder="Название встречи или события"
            value={title}
            maxLength={160}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
          />
          <button type="button" className="icon-btn" aria-label="Закрыть" onClick={onClose}>
            <Icon name="close" size={16} />
          </button>
        </header>

        <div className="event-editor-body">
          <div className="field-row-pair">
            <label className="field-row">
              <input
                type="checkbox"
                checked={allDay}
                onChange={(e) => setAllDay(e.target.checked)}
              />
              Весь день
            </label>
            <label className="field-row">
              <input
                type="checkbox"
                checked={done}
                onChange={(e) => setDone(e.target.checked)}
              />
              Выполнено
            </label>
          </div>

          <div className="field-grid">
            <label className="field">
              <span>Начало</span>
              <input
                type={allDay ? 'date' : 'datetime-local'}
                value={allDay ? toDateInput(start) : toLocalInput(start)}
                onChange={(e) => {
                  const next = fromLocalInput(
                    allDay ? `${e.target.value}T00:00` : e.target.value,
                  );
                  if (!next) return;
                  setStart(next);
                  // Dragging the start past the end is the common mistake, so
                  // the end follows along and keeps the original duration.
                  if (next > end) {
                    const durationMs = new Date(end).getTime() - new Date(start).getTime();
                    setEnd(new Date(new Date(next).getTime() + Math.max(durationMs, 0)).toISOString());
                  }
                }}
              />
            </label>

            <label className="field">
              <span>Окончание</span>
              <input
                type={allDay ? 'date' : 'datetime-local'}
                value={allDay ? toDateInput(end) : toLocalInput(end)}
                min={allDay ? toDateInput(start) : toLocalInput(start)}
                onChange={(e) => {
                  const next = fromLocalInput(
                    allDay ? `${e.target.value}T23:59` : e.target.value,
                  );
                  if (next) setEnd(next);
                }}
              />
            </label>

            <label className="field">
              <span>Напоминание</span>
              <select
                value={remind === null ? 'none' : String(remind)}
                onChange={(e) =>
                  setRemind(e.target.value === 'none' ? null : Number(e.target.value))
                }
              >
                {REMINDER_OPTIONS.map((option) => (
                  <option
                    key={option.label}
                    value={option.value === null ? 'none' : String(option.value)}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="field">
              <span>Доска</span>
              <select
                value={boardId ?? 'none'}
                onChange={(e) => setBoardId(e.target.value === 'none' ? null : e.target.value)}
              >
                <option value="none">Не привязана</option>
                {boards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {board.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="field">
            <span>План встречи</span>
            <textarea
              rows={4}
              placeholder="Что обсуждаем, к чему хотим прийти…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>

          <div className="field">
            <span>
              Задачи к этому сроку
              {tasks.length > 0 && (
                <em className="field-hint">
                  {' '}
                  выполнено {tasks.filter((t) => t.done).length} из {tasks.length}
                </em>
              )}
            </span>

            <ul className="task-list">
              {tasks.map((task, index) => (
                <li key={task.id ?? `draft-${index}`}>
                  <input
                    type="checkbox"
                    checked={task.done}
                    aria-label={`Отметить «${task.text}»`}
                    onChange={(e) =>
                      setTasks((current) =>
                        current.map((t, i) => (i === index ? { ...t, done: e.target.checked } : t)),
                      )
                    }
                  />
                  <input
                    className={`task-text ${task.done ? 'is-done' : ''}`}
                    value={task.text}
                    onChange={(e) =>
                      setTasks((current) =>
                        current.map((t, i) => (i === index ? { ...t, text: e.target.value } : t)),
                      )
                    }
                  />
                  <button
                    type="button"
                    aria-label={`Удалить задачу «${task.text}»`}
                    onClick={() => setTasks((current) => current.filter((_, i) => i !== index))}
                  >
                    <Icon name="close" size={13} />
                  </button>
                </li>
              ))}
            </ul>

            <div className="task-add">
              <input
                placeholder="Добавить задачу"
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTask();
                  }
                }}
              />
              <button type="button" onClick={addTask} disabled={newTask.trim() === ''}>
                <Icon name="plus" size={14} />
              </button>
            </div>
          </div>

          <div className="field">
            <span>Цвет</span>
            <div className="color-chips">
              {EVENT_COLORS.map((option) => (
                <button
                  key={option}
                  type="button"
                  className={`color-chip ${option === color ? 'is-active' : ''}`}
                  style={{ background: option }}
                  aria-label={`Цвет ${option}`}
                  aria-pressed={option === color}
                  onClick={() => setColor(option)}
                />
              ))}
            </div>
          </div>

          {error && <p className="form-error">{error}</p>}
        </div>

        <footer className="event-editor-foot">
          {event && (
            <button
              type="button"
              className="ghost-btn is-danger"
              onClick={async () => {
                if (!window.confirm(`Удалить событие «${event.title}»? Это нельзя отменить.`)) {
                  return;
                }
                await onDelete(event.id);
                onClose();
              }}
            >
              <Icon name="trash" size={15} />
              Удалить
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="ghost-btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="primary-btn" disabled={saving} onClick={submit}>
            <Icon name="check" size={15} />
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </footer>
      </div>
    </div>
  );
}
